import dayjs, { Dayjs } from 'dayjs';
import { UIEvent, useCallback, useMemo, useRef } from 'react';
import { unique } from 'remeda';
import { useScrollPaginateHandler } from 'src/utils/hooks/use-scroll-paginate-handler';
import { sortByDate } from 'src/utils/sorting';
import { useChatCommonData } from './common-data-context';
import { messageRegistry } from './index';
import { ChildStatus, MESSAGE_TYPE_IDS, MessageTypeId, TimelineEntry } from './types';
import { useLoadMoreGestureGate } from './use-load-more-gesture-gate';

export { useReadinessWithTimeout } from './use-readiness-with-timeout';

export function useMergedTimeline(): TimelineEntry<unknown>[] {
  const itemsByType = MESSAGE_TYPE_IDS.map((id) => {
    const reg = messageRegistry[id];

    return reg ? reg.useItems() : ([] as TimelineEntry<unknown>[]);
  });

  const chatPagination = messageRegistry[MessageTypeId.ChatMessage]?.usePaginationState?.();
  const hasMoreMessages = Boolean(chatPagination?.hasMore);

  return useMemo(
    () => {
      const chatItems = itemsByType[MESSAGE_TYPE_IDS.indexOf(MessageTypeId.ChatMessage)];
      let entries = itemsByType.flat();

      /*
        Only chat messages paginate; the other types arrive in full upfront. Without
        this window an incident or activity OLDER than the loaded message pages sits
        at the visual top of the thread pretending to be the oldest visible item,
        hiding the months of not-yet-loaded messages between it and the thread.
        Clip the side types to the loaded message window; every load-more widens it,
        and once messages are exhausted the clip lifts and the remainder renders.
      */
      if (hasMoreMessages && chatItems?.length) {
        const oldestLoadedMs = Math.min(
          ...chatItems
            .map((entry) => Date.parse(entry.timestamp))
            .filter((ms) => !Number.isNaN(ms)),
        );
        if (Number.isFinite(oldestLoadedMs)) {
          entries = entries.filter(
            (entry) =>
              entry.type === MessageTypeId.ChatMessage ||
              Date.parse(entry.timestamp) >= oldestLoadedMs,
          );
        }
      }

      return entries.toSorted(sortByDate('timestamp', true));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are the per-type arrays in stable order
    [...itemsByType, hasMoreMessages],
  );
}

export function useAllBlockingResolved(): { shouldRender: boolean } {
  const readinessByType = MESSAGE_TYPE_IDS.map((id) => {
    const reg = messageRegistry[id];

    const status = reg ? reg.useReadiness() : ChildStatus.Ready;
    return { reg, status };
  });

  // Error and Timeout are intentionally treated as "resolved enough" so that a
  // failed child does not permanently block sibling registrations from rendering.
  const shouldRender = readinessByType.every(({ reg, status }) => {
    if (!reg?.blockingForInitialRender) return true;
    return (
      status === ChildStatus.Ready ||
      status === ChildStatus.Refetching ||
      status === ChildStatus.Error ||
      status === ChildStatus.Timeout
    );
  });

  return { shouldRender };
}

export interface ChatPagination {
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => Promise<void>;
  reloadNewestPage: () => Promise<string[]>;
}

export function useChatPagination(): ChatPagination {
  const paginationStates = MESSAGE_TYPE_IDS.map((id) => {
    const reg = messageRegistry[id];
    return reg?.usePaginationState ? reg.usePaginationState() : undefined;
  });

  const loadMore = useChatLoadMore();
  const reloadNewestPage = useChatReloadNewestPage();

  const hasMore = paginationStates.some((s) => Boolean(s?.hasMore));
  const isLoadingMore = paginationStates.some((s) => Boolean(s?.isLoadingMore));

  return useMemo(
    () => ({ hasMore, isLoadingMore, loadMore, reloadNewestPage }),
    [hasMore, isLoadingMore, loadMore, reloadNewestPage],
  );
}

/*
  How early the next page starts loading, so it is usually in place before the reader
  reaches the top. A percentage of the thread pane's height rather than a pixel gap:
  in pixels, how early we fetch would depend on how tall the pane happens to be.
*/
const LOAD_MORE_THRESHOLD_PX = 200;
const LOAD_MORE_THRESHOLD_PERCENT_OF_HEIGHT = 100;

export function useChatScrollHandler(): (e: UIEvent) => Promise<void> {
  const { hasMore, loadMore } = useChatPagination();
  const gestureClock = useChatCommonData((s) => s.gestureClock);

  const handlerRef = useRef<((e: UIEvent | HTMLElement) => Promise<void>) | null>(null);
  const { isGestureFresh } = useLoadMoreGestureGate(gestureClock, (el) => {
    void handlerRef.current?.(el);
  });

  // one live gesture allows one page — see useLoadMoreGestureGate for why
  const gatedLoadMore = useCallback(async () => {
    if (!isGestureFresh()) {
      return;
    }
    await loadMore();
  }, [isGestureFresh, loadMore]);

  const handler = useScrollPaginateHandler({
    loadMore: gatedLoadMore,
    enabled: hasMore,
    thresholdPx: LOAD_MORE_THRESHOLD_PX,
    thresholdPercentOfHeight: LOAD_MORE_THRESHOLD_PERCENT_OF_HEIGHT,
    // a reader scrolling up steadily never "settles" until the top edge
    checkWhileScrolling: true,
    reversed: true,
  });
  handlerRef.current = handler;

  return handler;
}

export function useChatLoadMore(): () => Promise<void> {
  const loadMoreFnsByType = MESSAGE_TYPE_IDS.map((id) => {
    const reg = messageRegistry[id];

    return reg?.useLoadMore ? reg.useLoadMore() : undefined;
  });

  return useMemo(
    () => async () => {
      await Promise.all(loadMoreFnsByType.filter(Boolean).map((fn) => fn!()));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are the per-type fn refs in stable order
    loadMoreFnsByType,
  );
}

function useChatReloadNewestPage(): () => Promise<string[]> {
  const reloadFnsByType = MESSAGE_TYPE_IDS.map((id) => {
    const reg = messageRegistry[id];

    return reg?.useReloadNewestPage ? reg.useReloadNewestPage() : undefined;
  });

  return useMemo(
    () => async () => {
      const idsByType = await Promise.all(reloadFnsByType.filter(Boolean).map((fn) => fn!()));
      return idsByType.flat();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are the per-type fn refs in stable order
    reloadFnsByType,
  );
}

export function useTimelineBoundaryTimestamps(): Dayjs[] {
  const siblingItemArrays = MESSAGE_TYPE_IDS.filter(
    (id) => id !== MessageTypeId.ChatMessage,
  ).map((id) => {
    const reg = messageRegistry[id];
    return reg ? reg.useItems() : [];
  });

  return useMemo(
    () =>
      unique(siblingItemArrays.flat().map((entry) => entry.timestamp))
        .toSorted((a, b) => new Date(a).getTime() - new Date(b).getTime())
        .map((ts) => dayjs(ts)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are the per-type arrays in stable order
    siblingItemArrays,
  );
}
